import { useMemo, useState } from 'react';
import { isoWeek, isoWeekStart, type WeekFlowRow } from '../api';

interface Props {
  weeks: WeekFlowRow[];
  loading?: boolean;
}

/** View-model derived client-side from the server WeekFlowRow slice. */
interface WeekVM {
  weekLabel: string;
  weekStart: string;
  totalJobs: number;
  completedJobs: number;
  totalPoints: number;
  isCurrentWeek: boolean;
}

interface TooltipData {
  x: number;
  y: number;
  bucket: WeekVM;
}
type TooltipState = TooltipData | null;

export function WeekFlowChart({ weeks, loading }: Props) {
  const [tooltip, setTooltip] = useState<TooltipState>(null);

  // Derive V.N labels and isCurrentWeek from the ISO week_start dates.
  const buckets = useMemo<WeekVM[]>(() => {
    const currentWeekStart = isoWeekStart(new Date()).toISOString().slice(0, 10);
    return weeks.map((w) => {
      // week_start is "YYYY-MM-DD"; parse as a local date for ISO week math.
      const d = new Date(`${w.week_start}T00:00:00`);
      return {
        weekLabel: `V.${isoWeek(d)}`,
        weekStart: w.week_start,
        totalJobs: w.total_jobs,
        completedJobs: w.completed_jobs,
        totalPoints: w.total_points,
        isCurrentWeek: w.week_start === currentWeekStart,
      };
    });
  }, [weeks]);

  const panelHeader = (
    <div className="flex items-center justify-between mb-4">
      <span className="cmd-label">VECKOFLÖDE — 12 VECKOR</span>
      <span className="cmd-label text-[#8A6A47]">SCHEMALAGDA BESÖK</span>
    </div>
  );

  if (loading) {
    return (
      <div className="cmd-panel cmd-panel-inner rounded p-4 h-full">
        {panelHeader}
        <div className="flex items-end gap-1.5 h-32">
          {Array.from({ length: 12 }).map((_, i) => (
            <div
              key={i}
              className="flex-1 rounded-t animate-pulse"
              style={{
                height: `${30 + Math.random() * 50}%`,
                background: 'rgba(7,51,49,0.06)',
              }}
            />
          ))}
        </div>
      </div>
    );
  }

  const maxJobs = Math.max(...buckets.map((w) => w.totalJobs), 1);

  // Chart dimensions
  const SVG_W = 560;
  const SVG_H = 140;
  const PADDING_X = 8;
  const PADDING_TOP = 8;
  const LABEL_H = 20;
  const chartH = SVG_H - PADDING_TOP - LABEL_H;
  const barCount = Math.max(buckets.length, 1);
  const barGap = 4;
  const barW = (SVG_W - PADDING_X * 2 - barGap * (barCount - 1)) / barCount;

  return (
    <div className="cmd-panel cmd-panel-inner rounded p-4 h-full">
      {panelHeader}

      <div
        className="relative w-full overflow-x-auto"
        style={{ minHeight: SVG_H + 4 }}
      >
        <svg
          viewBox={`0 0 ${SVG_W} ${SVG_H}`}
          className="w-full"
          style={{ minWidth: 320, height: SVG_H }}
          onMouseLeave={() => setTooltip(null)}
        >
          {/* Subtle horizontal grid lines */}
          {[0.25, 0.5, 0.75, 1].map((frac) => {
            const y = PADDING_TOP + chartH * (1 - frac);
            return (
              <line
                key={frac}
                x1={PADDING_X}
                y1={y}
                x2={SVG_W - PADDING_X}
                y2={y}
                stroke="rgba(7,51,49,0.08)"
                strokeWidth="1"
              />
            );
          })}

          {buckets.map((bucket, i) => {
            const x = PADDING_X + i * (barW + barGap);
            const totalFrac = bucket.totalJobs / maxJobs;

            const totalH = chartH * totalFrac;
            const completedH = chartH * (bucket.completedJobs / maxJobs);
            const barY = PADDING_TOP + chartH - totalH;
            const completedY = PADDING_TOP + chartH - completedH;

            const isActive = tooltip?.bucket.weekStart === bucket.weekStart;

            return (
              <g key={bucket.weekStart}>
                {/* "Today" marker line */}
                {bucket.isCurrentWeek && (
                  <line
                    x1={x + barW / 2}
                    y1={PADDING_TOP}
                    x2={x + barW / 2}
                    y2={SVG_H - LABEL_H - 2}
                    stroke="#A07B52"
                    strokeWidth="1"
                    strokeDasharray="3,3"
                    opacity="0.7"
                  />
                )}

                {/* Total jobs bar (background) */}
                {totalH > 0 && (
                  <rect
                    x={x}
                    y={barY}
                    width={barW}
                    height={totalH}
                    rx="2"
                    fill={
                      isActive
                        ? 'rgba(7,51,49,0.22)'
                        : bucket.isCurrentWeek
                          ? 'rgba(7,51,49,0.16)'
                          : 'rgba(7,51,49,0.10)'
                    }
                  />
                )}

                {/* Completed overlay (lower green segment) */}
                {completedH > 0 && (
                  <rect
                    x={x}
                    y={completedY}
                    width={barW}
                    height={completedH}
                    rx="2"
                    fill={
                      isActive ? 'rgba(27,127,77,0.7)' : 'rgba(27,127,77,0.5)'
                    }
                  />
                )}

                {/* Invisible hover target */}
                <rect
                  x={x}
                  y={PADDING_TOP}
                  width={barW}
                  height={chartH}
                  fill="transparent"
                  onMouseEnter={(e) => {
                    const rect = (e.target as SVGRectElement)
                      .closest('svg')!
                      .getBoundingClientRect();
                    setTooltip({
                      x: e.clientX - rect.left,
                      y: e.clientY - rect.top,
                      bucket,
                    });
                  }}
                />

                {/* Week label */}
                <text
                  x={x + barW / 2}
                  y={SVG_H - 4}
                  textAnchor="middle"
                  fontSize="8"
                  fontFamily="ui-monospace, monospace"
                  letterSpacing="0.05em"
                  fill={bucket.isCurrentWeek ? '#A07B52' : 'rgba(96,124,110,0.9)'}
                >
                  {bucket.weekLabel}
                </text>

                {/* IDAG label */}
                {bucket.isCurrentWeek && (
                  <text
                    x={x + barW / 2}
                    y={PADDING_TOP - 2}
                    textAnchor="middle"
                    fontSize="7"
                    fontFamily="ui-monospace, monospace"
                    letterSpacing="0.1em"
                    fill="#A07B52"
                  >
                    IDAG
                  </text>
                )}
              </g>
            );
          })}
        </svg>

        {/* Tooltip */}
        {tooltip && (
          <div
            className="absolute pointer-events-none z-10 rounded px-2 py-1.5"
            style={{
              left: Math.min(tooltip.x + 8, 520),
              top: Math.max(tooltip.y - 60, 0),
              background: '#FFFFFF',
              border: '1px solid rgba(160,123,82,0.5)',
              boxShadow: '0 2px 8px rgba(7,51,49,0.10)',
              minWidth: 100,
            }}
          >
            <p className="cmd-label mb-1">{tooltip.bucket.weekLabel}</p>
            <p className="text-xs font-mono text-[#073331]">
              {tooltip.bucket.totalJobs} jobb
            </p>
            <p className="text-xs font-mono text-[#1B7F4D]">
              {tooltip.bucket.completedJobs} slutförda
            </p>
            <p className="text-xs font-mono" style={{ color: '#607C6E' }}>
              {tooltip.bucket.totalPoints.toFixed(1)} poäng
            </p>
          </div>
        )}
      </div>

      {/* Legend */}
      <div className="flex items-center gap-4 mt-2">
        <div className="flex items-center gap-1.5">
          <div className="w-3 h-2 rounded-sm" style={{ background: 'rgba(7,51,49,0.12)' }} />
          <span className="cmd-label">SCHEMALAGDA</span>
        </div>
        <div className="flex items-center gap-1.5">
          <div className="w-3 h-2 rounded-sm" style={{ background: 'rgba(27,127,77,0.55)' }} />
          <span className="cmd-label">SLUTFÖRDA</span>
        </div>
      </div>
    </div>
  );
}
